/**
 * Billing API client — wraps /billing/* endpoints.
 *
 * The five effective tiers returned by the server:
 *   'free' | 'lite' | 'pro' | 'lifetime_lite' | 'lifetime_pro'
 *
 * Tier + capabilities are loaded as part of /account (useAuth). Use this
 * module only for side-effect calls: start checkout, open portal, refresh
 * status on demand.
 */

import { getAuth } from 'firebase/auth';

const API = import.meta.env.VITE_API_URL || 'https://api.scrivar.com';

async function authHeaders() {
  const user = getAuth().currentUser;
  if (!user) throw new Error('Not signed in');
  const token = await user.getIdToken();
  return { Authorization: `Bearer ${token}` };
}

async function postJson(path, body) {
  const headers = { ...(await authHeaders()), 'Content-Type': 'application/json' };
  const res = await fetch(`${API}${path}`, {
    method: 'POST',
    headers,
    body: JSON.stringify(body || {}),
  });
  const data = await res.json().catch(() => ({}));
  if (!res.ok) {
    const err = new Error(data.message || `Request failed: ${res.status}`);
    err.status = res.status;
    err.code = data.code;
    err.upgrade = data.upgrade;
    throw err;
  }
  return data;
}

export async function getBillingStatus() {
  const headers = await authHeaders();
  const res = await fetch(`${API}/billing/status`, { headers });
  const data = await res.json();
  if (!res.ok) throw new Error(data.message || 'Status failed');
  return data;
}

/**
 * Start a Stripe Checkout for one of: 'lite' | 'pro' | 'cloud_ai'.
 * Redirects the browser to Stripe on success.
 */
export async function startCheckout(product) {
  const data = await postJson('/billing/checkout', { product });
  if (!data.checkoutUrl) throw new Error('Stripe did not return a checkout URL.');
  window.location.href = data.checkoutUrl;
}

/**
 * Open the Stripe Customer Portal in the current tab so the user can update
 * payment methods, cancel, or download invoices. Redirects on success.
 */
export async function openBillingPortal() {
  const data = await postJson('/billing/portal', {});
  if (!data.portalUrl) throw new Error('Stripe did not return a portal URL.');
  window.location.href = data.portalUrl;
}

// ─── Tier classification helpers ─────────────────────────────────────────────

export const ALL_TIERS = ['free', 'lite', 'pro', 'lifetime_lite', 'lifetime_pro'];
export const WEBSITE_TIERS = ['lite', 'pro'];
export const LIFETIME_TIERS = ['lifetime_lite', 'lifetime_pro'];
export function isWebsiteTier(tier) { return WEBSITE_TIERS.includes(tier); }
export function isLifetimeTier(tier) { return LIFETIME_TIERS.includes(tier); }

/**
 * Compose a human-readable plan label from the resolved account object.
 * Returns { label, price, tagline }.
 */
export function tierLabel(account) {
  const tier = account?.tier || 'free';
  const hasAddon = Boolean(account?.addonSubscription);
  switch (tier) {
    case 'pro':
      return { label: 'Scrivar Pro', price: '$12.99/mo', tagline: 'Unlimited eSign + 20 GB cloud + AI tools' };
    case 'lite':
      return { label: 'Scrivar Lite', price: '$7.99/mo', tagline: 'All PDF tools + eSign + 5 GB cloud' };
    case 'lifetime_pro':
      return {
        label: hasAddon ? 'Pro Lifetime + Cloud & AI' : 'Pro Lifetime',
        price: hasAddon ? '+$4.99/mo' : 'Lifetime',
        tagline: hasAddon
          ? 'Full PDF editor + unlimited eSign, with cloud sync + AI'
          : 'Full PDF editor + unlimited eSign — one-time, no subscription',
      };
    case 'lifetime_lite':
      return {
        label: hasAddon ? 'Lite Lifetime + Cloud & AI' : 'Lite Lifetime',
        price: hasAddon ? '+$4.99/mo' : 'Lifetime',
        tagline: hasAddon
          ? 'Core PDF tools + 5 eSign/mo, with cloud sync + AI'
          : 'Core PDF tools + 5 eSign/mo — one-time, no subscription',
      };
    case 'free':
    default:
      return { label: 'Free', price: '$0', tagline: 'Core PDF tools with daily limits' };
  }
}

// Returns the checkout product key the user should be offered for a missing
// capability. Mirrors the server's requireCap suggestUpgrade.
export function upgradeProductFor(tier, capability) {
  if (isLifetimeTier(tier)) {
    if (capability === 'ai_tools' || capability === 'cloud_bytes' || capability === 'cloud') {
      return 'cloud_ai';
    }
    return 'pro_lifetime';
  }
  if (capability === 'esign_self' || capability === 'esign_request') return 'lite_lifetime';
  return 'pro_lifetime';
}

export function formatBytes(bytes) {
  if (!bytes || bytes <= 0) return '0 B';
  const units = ['B', 'KB', 'MB', 'GB', 'TB'];
  const exp = Math.min(units.length - 1, Math.floor(Math.log(bytes) / Math.log(1024)));
  const value = bytes / Math.pow(1024, exp);
  return `${value.toFixed(value < 10 && exp > 0 ? 1 : 0)} ${units[exp]}`;
}

export function formatLimit(value, formatter) {
  if (value === null) return 'Unlimited';
  if (value === 0 || value === false) return 'Not included';
  if (typeof value === 'number') return formatter ? formatter(value) : String(value);
  return 'Included';
}
